import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { notesAPI } from 'services/apisService';
import { contactsAPI } from 'services/apisService';
import NavigationFooter from './Contact/NavigationFooter';
import HeaderContactsDetails from './Contact/HeaderContactsDetails';
import CompanyInformation from './CompanyInformation';

const ContactDetailsModal = ({ 
  isOpen, 
  onClose, 
  contact, 
  onNavigate, 
  contacts,
  isLead = false,
  onNoteAdded,
  onNoteUpdated,
  onLeadUpdated,
  onEngagementUpdated,
  onContactUpdate 
}) => {
   const actualContact = useMemo(() => {
    if (isLead && contact?.Contact) {
      return {
        ...contact.Contact,
        engagement_stage: contact.engagement_stage
      };
    }
    return contact;
  }, [isLead, contact]);
  const modalRef = useRef(null);
  const [animationClass, setAnimationClass] = useState('translate-x-full');
  const leadStatus = isLead ? contact?.status : null;
  const currentIndex = contacts.findIndex(c => {
  if (isLead) {
    return c === contact?.id;
  }
  return c.id === actualContact?.id; 
});
  const leadContactId = isLead ? contact?.id : null;
  const hasNext = currentIndex < contacts.length - 1;
  const hasPrevious = currentIndex > 0;
  const [notes, setNotes] = useState([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [showAddNoteForm, setShowAddNoteForm] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [noteError, setNoteError] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteContent, setEditNoteContent] = useState('');
  const [isUpdatingNote, setIsUpdatingNote] = useState(false);
  const [followupData, setFollowupData] = useState({
  lastFollowupCall: '',
  lastFollowupMail: '',
  nextFollowupMail: '',
  nextFollowupCall: '',
  tempLastFollowupCall: '',
  tempLastFollowupMail: '',
  tempNextFollowupMail: '',
  tempNextFollowupCall: '',
  showLastFollowupCallEdit: false,
  showLastFollowupMailEdit: false,
  showNextFollowupMailEdit: false,
  showNextFollowupCallEdit: false
});
  const [isUpdatingFollowup, setIsUpdatingFollowup] = useState(false);
  const [followupError, setFollowupError] = useState('');
  const [isLoadingFollowup, setIsLoadingFollowup] = useState(false);

   const handleContactUpdate = (updatedContactData) => {
    if (onContactUpdate) {
      onContactUpdate(updatedContactData);
    }
  };
  
 useEffect(() => {
  if (isOpen) {
    document.body.style.overflow = 'hidden'; 
    document.body.style.paddingRight = '15px'; 
    setTimeout(() => {
      setAnimationClass('translate-x-0'); 
    }, 10);

    if (actualContact?.id) {
      setIsLoadingFollowup(true); 
      setIsLoadingNotes(true);
      setTimeout(() => {
        setFollowupData({
          lastFollowupCall: actualContact.lastFollowupCall || '',
          lastFollowupMail: actualContact.lastFollowupMail || '',
          nextFollowupMail: actualContact.nextFollowupMail || '',
          nextFollowupCall: actualContact.nextFollowupCall || '',
          tempLastFollowupCall: '',
          tempLastFollowupMail: '',
          tempNextFollowupMail: '',
          tempNextFollowupCall: '',
          showLastFollowupCallEdit: false,
          showLastFollowupMailEdit: false,
          showNextFollowupMailEdit: false,
          showNextFollowupCallEdit: false
        });
        setIsLoadingFollowup(false);
      },100);
      
      setTimeout(() => {
        setNotes(actualContact?.notes || []);
        setIsLoadingNotes(false);
      },100);
    }
  } else {
    document.body.style.overflow = 'auto'; 
    document.body.style.paddingRight = '';
    setAnimationClass('translate-x-full'); 
    resetModalState();
  }
  
  return () => {
    document.body.style.overflow = 'auto'; 
    document.body.style.paddingRight = ''; 
    resetModalState();
  };
}, [isOpen, actualContact]);

 const resetModalState = () => {
  setShowAddNoteForm(false);
  setNewNoteContent('');
  setEditingNoteId(null);
  setEditNoteContent('');
  setNoteError('');
  setFollowupError('');
  setNotes([]);
  setIsLoadingNotes(false);
  setIsLoadingFollowup(false);
 };

const handleUpdateFollowup = async (field, value, amPm) => {
  setIsUpdatingFollowup(true);
  setFollowupError('');
  
  try {
    let finalValue = '';
    
    if (value) {
      const dateParts = value.split('T')[0].split('-'); 
      const year = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1; 
      const day = parseInt(dateParts[2], 10);
      
      finalValue = new Date(Date.UTC(year, month, day)).toISOString();
    }
    
    const updateData = {
      [field]: finalValue
    };
    
    await contactsAPI.updateContact(actualContact.id, updateData);
    
    setFollowupData(prev => ({
      ...prev,
      [field]: finalValue,
      [`temp${field.charAt(0).toUpperCase() + field.slice(1)}`]: ''
    }));
    
  } catch (error) {
    console.error(`Error updating ${field}:`, error);
    setFollowupError('Failed to update followup info. Please try again.');
  } finally {
    setIsUpdatingFollowup(false);
  }
};

  const handleAddNote = async (e) => {
  e.preventDefault();
  
  if (!newNoteContent.trim()) {
    setNoteError('Note content cannot be empty');
    return;
  }
  
  setIsSubmittingNote(true);
  setNoteError('');
  
  try {
    const noteData = {
      contactId: actualContact.id,
      content: newNoteContent.trim()
    };
    
    const newNote = await notesAPI.addNote(noteData);
    setNotes(prevNotes => [newNote, ...prevNotes]);
    
    if (onNoteAdded && isLead) {
      onNoteAdded(contact.id, newNote);
    }
    
    setNewNoteContent('');
    setShowAddNoteForm(false);
  } catch (error) {
    console.error('Error adding note:', error);
    setNoteError('Failed to add note. Please try again.');
  } finally {
    setIsSubmittingNote(false);
  }
};

  const handleUpdateNote = async (e) => {
  e.preventDefault();
  
  if (!editNoteContent.trim()) {
    setNoteError('Note content cannot be empty');
    return;
  }
  
  setIsUpdatingNote(true);
  setNoteError('');
  
  try {
    const updatedNote = await notesAPI.updateNote(editingNoteId, {
      content: editNoteContent.trim()
    });
    
    setNotes(prevNotes => 
      prevNotes.map(note => 
        note.id === editingNoteId ? updatedNote : note
      )
    );
    
    if (onNoteUpdated && isLead) {
      const currentNotes = notes.map(note => 
        note.id === editingNoteId ? updatedNote : note
      );
      
      const sortedNotes = [...currentNotes].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      
      if (sortedNotes.length > 0 && sortedNotes[0].id === editingNoteId) {
        onNoteUpdated(contact.id, updatedNote);
      }
    }
    
    setEditingNoteId(null);
    setEditNoteContent('');
  } catch (error) {
    console.error('Error updating note:', error);
    setNoteError('Failed to update note. Please try again.');
  } finally {
    setIsUpdatingNote(false);
  }
};

  const getStatusDetails = useCallback(() => {
    const statusOptions = [
      { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800', bgColor: 'bg-blue-50' },
      { value: 'attempted_contact', label: 'Attempted Contact', color: 'bg-purple-100 text-purple-800', bgColor: 'bg-purple-50' },
      { value: 'follow_up_scheduled', label: 'Follow-up Scheduled', color: 'bg-amber-100 text-amber-800', bgColor: 'bg-amber-50' },
      { value: 'interested', label: 'Interested', color: 'bg-green-100 text-green-800', bgColor: 'bg-green-50' },
      { value: 'not_interested', label: 'Not Interested', color: 'bg-red-100 text-red-800', bgColor: 'bg-red-50' },
      { value: 'wrong_contact', label: 'Wrong Contact', color: 'bg-gray-100 text-gray-800', bgColor: 'bg-gray-50' },
      { value: 'converted_to_lead', label: 'Converted to Lead', color: 'bg-green-400 text-white', bgColor: 'bg-green-50' }
    ];
    
    return statusOptions.find(opt => opt.value === (actualContact?.status || 'new')) || statusOptions[0];
  }, [actualContact]);

  const handleClose = () => {
    setAnimationClass('translate-x-full');
    setTimeout(() => {
      onClose();
    }, 300); 
  };

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);
  };

  useEffect(() => {
  const handleKeyDown = (e) => {
    if (!isOpen) return;

    if (e.key === 'Escape') {
      handleClose();
    }
  };

  document.addEventListener('keydown', handleKeyDown);
  return () => document.removeEventListener('keydown', handleKeyDown);
}, [isOpen, handleClose]);


  const statusDetails = getStatusDetails();

  if (!isOpen || !actualContact) return null;

  return (
    <div 
      className={`fixed inset-0 z-50 ${isOpen ? 'backdrop-blur-[4px] bg-[#0000]/40' : ''} transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
      onClick={handleBackdropClick}
    >
      {/* Close button - positioned outside drawer on the left */}
      <button
        onClick={handleClose}
        className={`fixed top-6 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all duration-300 z-[60] ${animationClass === 'translate-x-0' ? 'left-[calc(15%-3rem)]' : 'left-[-4rem]'}`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <div 
        ref={modalRef} 
        className={`fixed top-0 right-0 h-full w-[70%] bg-white shadow-xl flex flex-col transform transition-transform duration-300 ease-in-out ${animationClass} rounded-tl-3xl rounded-bl-3xl`}
        style={{ minWidth: '85%' }}
      >
        <HeaderContactsDetails 
        contact={actualContact} 
        statusDetails={statusDetails} 
        handleClose={handleClose}
        isLead={isLead}
        leadStatus={leadStatus}
        leadContactId={leadContactId}
        onLeadUpdated={onLeadUpdated}
        onEngagementUpdated={onEngagementUpdated}
        onContactUpdate={handleContactUpdate}
      />
        
        <div className={`flex-1 overflow-y-auto px-6 py-7 ${statusDetails.bgColor}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mx-auto h-full">
           <CompanyInformation onContactUpdate={handleContactUpdate} contact={actualContact} isLead={isLead} leadContactId={leadContactId} onLeadUpdated={onLeadUpdated}/>

            <div className="bg-white rounded-lg shadow-md border border-[#e5f0f9] p-6 hover:shadow-lg transition-shadow duration-300">
              <h3 className="text-lg font-semibold mb-4 text-[#3985b6] border-b border-[#e5f0f9] pb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Follow-up Information
              </h3>
              
              {followupError && (
                <div className="mb-3 p-2 bg-red-100 text-red-800 rounded-md">
                  {followupError}
                </div>
              )}
              
              <div className="space-y-7 max-h-[51vh] overflow-hidden overflow-y-auto pr-1">
                {isLoadingFollowup ? (
                  <div className="flex justify-center p-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#3985b6]"></div>
                  </div>
                ) : (
                  <>
                  {/* Next Follow-up Call Section */}
                  <div>
                   <p className="text-sm font-bold text-[#3985b6] uppercase tracking-wider mb-1 flex items-center">
                    Next Follow-up Call
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </p>
                    
                    {followupData.nextFollowupCall && (
                    <div className="flex items-center mb-3">
                      <p className="text-gray-800 bg-gray-50 p-2 rounded flex-grow">
                        {new Date(followupData.nextFollowupCall).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                        <button 
                          onClick={() => setFollowupData(prev => ({
                            ...prev,
                            showNextFollowupCallEdit: true
                          }))} 
                          className="ml-2 text-gray-500 hover:text-[#3985b6]"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </div>
                    )}
                    
                    {(!followupData.nextFollowupCall || followupData.showNextFollowupCallEdit) && (
                      <div className="flex space-x-2">
                        <div className="flex-grow">
                          <input
                            type="date"
                            value={followupData.tempNextFollowupCall?.split('T')[0] || ''}
                            onChange={(e) => setFollowupData(prev => ({
                              ...prev,
                              tempNextFollowupCall: e.target.value ? `${e.target.value}T00:00:00` : ''
                            }))}
                            className="w-full text-gray-800 bg-gray-50 p-2 rounded border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#3985b6]"
                            style={{ minHeight: '40px' }}
                            disabled={isUpdatingFollowup}
                          />
                        </div>
                        
                        <button
                          onClick={() => {
                            handleUpdateFollowup('nextFollowupCall', followupData.tempNextFollowupCall, '');
                            setFollowupData(prev => ({
                              ...prev,
                              showNextFollowupCallEdit: false,
                              tempNextFollowupCall: ''
                            }));
                          }}
                          className="bg-[#3985b6] text-white px-4 rounded hover:bg-[#2d6f9a] disabled:opacity-50"
                          disabled={isUpdatingFollowup || !followupData.tempNextFollowupCall}
                        >
                          {isUpdatingFollowup ? 'Saving...' : 'Save'}
                        </button>
                        
                        {followupData.showNextFollowupCallEdit && (
                          <button
                            onClick={() => setFollowupData(prev => ({
                              ...prev,
                              showNextFollowupCallEdit: false,
                              tempNextFollowupCall: ''
                            }))}
                            className="border border-gray-300 text-gray-600 px-4 rounded hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Last Follow-up Call Section */}
                  <div>
                      <p className="text-sm font-bold text-[#3985b6] uppercase tracking-wider mb-1 flex items-center">
                    Last Follow-up Call
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </p>
                    
                    {followupData.lastFollowupCall && (
                      <div className="flex items-center mb-3">
                        <p className="text-gray-800 bg-gray-50 p-2 rounded flex-grow">
                          {new Date(followupData.lastFollowupCall).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                        <button 
                          onClick={() => setFollowupData(prev => ({
                            ...prev,
                            showLastFollowupCallEdit: true
                          }))} 
                          className="ml-2 text-gray-500 hover:text-[#3985b6]"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </div>
                    )}
                    
                    {(!followupData.lastFollowupCall || followupData.showLastFollowupCallEdit) && (
                      <div className="flex space-x-2">
                        <div className="flex-grow">
                          <input
                            type="date"
                            value={followupData.tempLastFollowupCall?.split('T')[0] || ''}
                            onChange={(e) => setFollowupData(prev => ({
                              ...prev,
                              tempLastFollowupCall: e.target.value ? `${e.target.value}T00:00:00` : ''
                            }))}
                            className="w-full text-gray-800 bg-gray-50 p-2 rounded border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#3985b6]"
                            style={{ minHeight: '40px' }}
                            disabled={isUpdatingFollowup}
                          />
                        </div>
                        
                        <button
                          onClick={() => {
                            handleUpdateFollowup('lastFollowupCall', followupData.tempLastFollowupCall, '');
                            setFollowupData(prev => ({
                              ...prev,
                              showLastFollowupCallEdit: false,
                              tempLastFollowupCall: ''
                            }));
                          }}
                          className="bg-[#3985b6] text-white px-4 rounded hover:bg-[#2d6f9a] disabled:opacity-50"
                          disabled={isUpdatingFollowup || !followupData.tempLastFollowupCall}
                        >
                          {isUpdatingFollowup ? 'Saving...' : 'Save'}
                        </button>
                        
                        {followupData.showLastFollowupCallEdit && (
                          <button
                            onClick={() => setFollowupData(prev => ({
                              ...prev,
                              showLastFollowupCallEdit: false,
                              tempLastFollowupCall: ''
                            }))}
                            className="border border-gray-300 text-gray-600 px-4 rounded hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <hr className="border-t-2 border-[#69a5d5] my-4" />

                  {/* Next Follow-up Mail Section */}
                  <div>
                    <p className="text-sm font-bold text-[#3985b6] uppercase tracking-wider mb-1 flex items-center">
                      Next Follow-up Mail
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </p>
                    
                    {followupData.nextFollowupMail && (
                      <div className="flex items-center mb-3">
                        <p className="text-gray-800 bg-gray-50 p-2 rounded flex-grow">
                          {new Date(followupData.nextFollowupMail).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                        <button 
                          onClick={() => setFollowupData(prev => ({
                            ...prev,
                            showNextFollowupMailEdit: true
                          }))} 
                          className="ml-2 text-gray-500 hover:text-[#3985b6]"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </div>
                    )}
                    
                    {(!followupData.nextFollowupMail || followupData.showNextFollowupMailEdit) && (
                      <div className="flex space-x-2">
                        <div className="flex-grow">
                          <input
                            type="date"
                            value={followupData.tempNextFollowupMail?.split('T')[0] || ''}
                            onChange={(e) => setFollowupData(prev => ({
                              ...prev,
                              tempNextFollowupMail: e.target.value ? `${e.target.value}T00:00:00` : ''
                            }))}
                            className="w-full text-gray-800 bg-gray-50 p-2 rounded border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#3985b6]"
                            style={{ minHeight: '40px' }}
                            disabled={isUpdatingFollowup}
                          />
                        </div>
                        
                        <button
                          onClick={() => {
                            handleUpdateFollowup('nextFollowupMail', followupData.tempNextFollowupMail, '');
                            setFollowupData(prev => ({
                              ...prev,
                              showNextFollowupMailEdit: false,
                              tempNextFollowupMail: ''
                            }));
                          }}
                          className="bg-[#3985b6] text-white px-4 rounded hover:bg-[#2d6f9a] disabled:opacity-50"
                          disabled={isUpdatingFollowup || !followupData.tempNextFollowupMail}
                        >
                          {isUpdatingFollowup ? 'Saving...' : 'Save'}
                        </button>
                        
                        {followupData.showNextFollowupMailEdit && (
                          <button
                            onClick={() => setFollowupData(prev => ({
                              ...prev,
                              showNextFollowupMailEdit: false,
                              tempNextFollowupMail: ''
                            }))}
                            className="border border-gray-300 text-gray-600 px-4 rounded hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Last Follow-up Mail Section */}
                  <div>
                    <p className="text-sm font-bold text-[#3985b6] uppercase tracking-wider mb-1 flex items-center">
                      Last Follow-up Mail
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </p>
                    
                    {followupData.lastFollowupMail && (
                      <div className="flex items-center mb-3">
                        <p className="text-gray-800 bg-gray-50 p-2 rounded flex-grow">
                          {new Date(followupData.lastFollowupMail).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                        <button 
                          onClick={() => setFollowupData(prev => ({
                            ...prev,
                            showLastFollowupMailEdit: true
                          }))} 
                          className="ml-2 text-gray-500 hover:text-[#3985b6]"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </div>
                    )}
                    
                    {/* Show input form when no date exists OR when editing */}
                    {(!followupData.lastFollowupMail || followupData.showLastFollowupMailEdit) && (
                      <div className="flex space-x-2">
                        <div className="flex-grow">
                          <input
                            type="date"
                            value={followupData.tempLastFollowupMail?.split('T')[0] || ''}
                            onChange={(e) => setFollowupData(prev => ({
                              ...prev,
                              tempLastFollowupMail: e.target.value ? `${e.target.value}T00:00:00` : ''
                            }))}
                            className="w-full text-gray-800 bg-gray-50 p-2 rounded border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#3985b6]"
                            style={{ minHeight: '40px' }}
                            disabled={isUpdatingFollowup}
                          />
                        </div>
                        
                        <button
                          onClick={() => {
                            handleUpdateFollowup('lastFollowupMail', followupData.tempLastFollowupMail, '');
                            setFollowupData(prev => ({
                              ...prev,
                              showLastFollowupMailEdit: false,
                              tempLastFollowupMail: ''
                            }));
                          }}
                          className="bg-[#3985b6] text-white px-4 rounded hover:bg-[#2d6f9a] disabled:opacity-50"
                          disabled={isUpdatingFollowup || !followupData.tempLastFollowupMail}
                        >
                          {isUpdatingFollowup ? 'Saving...' : 'Save'}
                        </button>
                        
                        {followupData.showLastFollowupMailEdit && (
                          <button
                            onClick={() => setFollowupData(prev => ({
                              ...prev,
                              showLastFollowupMailEdit: false,
                              tempLastFollowupMail: ''
                            }))}
                            className="border border-gray-300 text-gray-600 px-4 rounded hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  </>
                )}
              </div>
            </div>

            {/* Notes Section */}
            <div className="bg-white rounded-lg shadow-md border border-[#e5f0f9] p-6 hover:shadow-lg transition-shadow duration-300 md:col-span-1">
              <h3 className="text-lg font-semibold mb-4 text-[#3985b6] border-b border-[#e5f0f9] pb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Notes
              </h3>
              
              {noteError && (
                <div className="mb-3 p-2 bg-red-100 text-red-800 rounded-md">
                  {noteError}
                </div>
              )}
              
              {showAddNoteForm ? (
                <form onSubmit={handleAddNote} className="mb-4">
                  <textarea
                  value={newNoteContent}
                  onChange={(e) => setNewNoteContent(e.target.value)}
                  placeholder="Enter your note here..."
                  className="w-full p-3 border border-[#e5f0f9] rounded-md focus:ring-2 focus:ring-[#3985b6] focus:border-transparent"
                  rows={4}
                  required
                  disabled={isSubmittingNote}
                  style={{ whiteSpace: 'normal', overflow: 'hidden' }}
                />
                  <div className="flex justify-end mt-2 space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddNoteForm(false);
                        setNewNoteContent('');
                        setNoteError('');
                      }}
                      className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                      disabled={isSubmittingNote}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-[#3985b6] text-white rounded-md hover:bg-[#2d6f9a] disabled:opacity-50"
                      disabled={isSubmittingNote}
                    >
                      {isSubmittingNote ? 'Saving...' : 'Save Note'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="mb-4">
                  <button 
                    onClick={() => setShowAddNoteForm(true)}
                    className="flex items-center text-[#3985b6] hover:text-[#2d6f9a] group"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 group-hover:scale-110 transition-transform" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                    </svg>
                    Add Note
                  </button>
                </div>
              )}
              
              <div className="space-y-3 max-h-[45vh] overflow-y-auto pr-1">
                {notes.length > 0 ? (
                notes.map((note) => (
                  <div key={note.id} className="p-3 bg-[#f5fafd] rounded-md border border-[#e5f0f9] hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-medium text-[#3985b6]">
                        {note.user?.name || 'User'}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => {
                            setEditingNoteId(note.id);
                            setEditNoteContent(note.content);
                          }}
                          className="text-gray-500 hover:text-[#3985b6] p-1 rounded-full hover:bg-[#f0f7fc]"
                          title="Edit note"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <div className="text-xs text-gray-500">
                          {formatDate(note.created_at)}
                        </div>
                      </div>
                    </div>
                    
                  {editingNoteId === note.id ? (
                    <form onSubmit={handleUpdateNote}>
                      <textarea
                        value={editNoteContent}  
                        onChange={(e) => setEditNoteContent(e.target.value)} 
                        placeholder="Enter your note here..."
                        className="w-full p-3 border border-[#e5f0f9] rounded-md focus:ring-2 focus:ring-[#3985b6] focus:border-transparent"
                        rows={4}
                        required
                        disabled={isUpdatingNote} 
                        style={{ whiteSpace: 'normal', overflow: 'hidden' }}
                      />
                      <div className="flex justify-end mt-2 space-x-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingNoteId(null);
                            setEditNoteContent('');
                          }}
                          className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                          disabled={isUpdatingNote}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-3 py-1 text-sm bg-[#3985b6] text-white rounded-md hover:bg-[#2d6f9a] disabled:opacity-50"
                          disabled={isUpdatingNote}
                        >
                          {isUpdatingNote ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <p className="note-wrap text-gray-700">
                      {note.content}
                    </p>
                  )}
                  </div>
                ))
                ) : (
                <div className="p-4 bg-[#f5fafd] rounded-md border border-[#e5f0f9]">
                {isLoadingNotes ? (
                  <div className="flex justify-center p-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#3985b6]"></div>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No notes available</p>
                )}
              </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
         <NavigationFooter 
        currentIndex={currentIndex} 
        contacts={contacts} 
        hasNext={hasNext} 
        hasPrevious={hasPrevious} 
        onNavigate={onNavigate} 
        isLead={isLead}
      />
      </div>
    </div>
  );
};

export default React.memo(ContactDetailsModal);